'use client';

/**
 * ReferralStatsCards - 4 stat cards for referral dashboard
 * Shows: Active Referrals, Next Payout, Highest Commission, Lifetime Earnings
 */

import React from 'react';
import { Users, DollarSign, Trophy, Wallet } from 'lucide-react';
import { GlassCard, CardContent } from '@/components/ui/glass-card';
import type { ReferralStats } from '../types';
import { formatPrice } from '@/lib/utils/format-price';

interface ReferralStatsCardsProps {
  stats: ReferralStats;
}

export function ReferralStatsCards({ stats }: ReferralStatsCardsProps) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const cards = [
    {
      title: 'Active Referrals',
      value: stats.payingActiveReferrals.toString(),
      subtitle:
        stats.referralsToNextLevel > 0
          ? `${stats.referralsToNextLevel} to next level`
          : 'Max level reached!',
      icon: Users,
      iconColor: 'text-amber-500',
      iconBg: 'bg-amber-500/10',
    },
    {
      title: 'Next Payout',
      value: formatPrice(stats.nextPayout.amount, 'EUR', { minimumFractionDigits: 2 }),
      subtitle: `Available ${formatDate(stats.nextPayout.availableDate)}`,
      icon: DollarSign,
      iconColor: 'text-emerald-500',
      iconBg: 'bg-emerald-500/10',
    },
    {
      title: 'Highest Commission',
      value: formatPrice(stats.highestCommission.amount, 'EUR', { minimumFractionDigits: 2 }),
      subtitle:
        stats.highestCommission.sources.length > 0
          ? stats.highestCommission.sources.join(', ')
          : 'No commissions yet',
      icon: Trophy,
      iconColor: 'text-yellow-500',
      iconBg: 'bg-yellow-500/10',
    },
    {
      title: 'Lifetime Earnings',
      value: formatPrice(stats.lifetimeEarnings, 'EUR', { minimumFractionDigits: 2 }),
      subtitle: `${stats.totalReferrals} referred ${
        stats.totalReferrals === 1 ? 'client' : 'clients'
      }`,
      icon: Wallet,
      iconColor: 'text-brand-primary',
      iconBg: 'bg-brand-primary/10',
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card) => (
        <GlassCard key={card.title} className="overflow-hidden">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="text-sm text-gray-500 dark:text-gray-400">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-500 truncate max-w-[150px]">
                  • {card.subtitle}
                </p>
              </div>
              <div className={`p-2 rounded-lg ${card.iconBg}`}>
                <card.icon className={`h-5 w-5 ${card.iconColor}`} />
              </div>
            </div>
          </CardContent>
        </GlassCard>
      ))}
    </div>
  );
}
